// © Microsoft Corporation. All rights reserved.

import { CommentConnector, ComponentConnector, FollowInfo, PopupConnector, UsersConnector } from "../../Social.props";
import { CommentListTelemetryObjects, commentListTelemetryContracts } from "./CommentList.telemetry";
import { Comments, Content, User } from "@msnews/social-data-service";
import { chevron_down, chevron_up } from "../../utils/icons";

import CommentBox from "../comment-box/CommentBox";
import CommentItem from "../comment-item/CommentItem";
import React from "react";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import SortBox from "../../common/sort-box/SortBox";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import { addOrUpdateTelemetryObject } from "../../Social.telemetry";

export interface CommentListProps {
    componentConnector: ComponentConnector;
    comments: Comments;
    totalCommentCount: number;
    commentConnector: CommentConnector;
    contentId: string;
    managedClasses?: SocialClassNameContract;
    contentDisabled: boolean;
    me: User;
    usersConnector: UsersConnector;
    popupConnector: PopupConnector;
    users: { [id: string]: FollowInfo;}; // dictionary definening
    t: SocialLocalizedStrings;
    market: string;
    content: Content;
    mainTelemetryObject: TelemetryObjectBase;
    theme: string;
    responsiveClass: string;
    isExpanded: boolean;
    commentDisplayType?: string;
    onCommentBoxInitialized?: (commentBox: HTMLDivElement) => void;
    specificCommentId?: string;
}

interface CommentListState {
    sortType: string;
    showMoreClicked: boolean;
}

export default class CommentList extends React.Component<CommentListProps, CommentListState> {
    telemetryObjects: CommentListTelemetryObjects;
    static sortTypes = {
        Rating: "CommentSortByTopResponseTxt",
        Time: "CommentSortByNewestTxt",
        "Time asc": "CommentSortByOldestTxt"
    };

    constructor(props) {
        super(props);

        this.state = {
            showMoreClicked: false,
            sortType: this.props.specificCommentId ? "Time" : "Rating"
        };

        this.submitCommentAndUpdateState = this.submitCommentAndUpdateState.bind(this);
    }

    showMoreClicked(e) {
        this.setState({
            showMoreClicked: true
        });
        const top = 10;
        const skip = this.props.comments.skip + this.props.comments.top;

        const showMoreComments = this.props.commentConnector.showMoreComments;
        if (!showMoreComments) { return console.warn("Missing props 'showMoreComments'"); }

        showMoreComments(this.props.contentId, top, skip, this.state.sortType);
    }

    getSortedComments(sortType) {

        this.setState({
            showMoreClicked: false,
            sortType: sortType
        });
        const top = 2;
        const onSortTypeChanged = this.props.commentConnector.onSortTypeChanged;

        if (!onSortTypeChanged) { return console.warn("Missing props 'commentConnector.onSortTypeChanged"); }
        onSortTypeChanged(this.props.contentId, top, sortType);
    }

    async submitCommentAndUpdateState(value) {
        return await this.props.commentConnector.submitComment(this.props.content, value);
    }

    render() {
        return this.props.isExpanded ? this.renderFull() : this.renderMinimal();
    }

    renderMinimal() {
        const { comments, managedClasses, mainTelemetryObject, content } = this.props;

        /** Generate Telemetry Objects */
        this.telemetryObjects = {
            mainItem: addOrUpdateTelemetryObject(mainTelemetryObject, {...commentListTelemetryContracts.mainItem}, {id: content && content.cmsId}, this.telemetryObjects && this.telemetryObjects.mainItem)
        };

        return (
            <ul className={managedClasses.commentListContent}>
                {comments && comments.list && this.renderCommentItem(comments.list[0], true)}
            </ul>
        );
    }

    renderFull() {
        const { onCommentBoxInitialized, componentConnector, comments, contentDisabled, me, t, managedClasses, usersConnector, popupConnector,
            users, market, totalCommentCount, content, mainTelemetryObject, theme, contentId, commentConnector } = this.props;
        const moreCommentCount = comments && comments.moreCount;
        const submitCommentAndUpdateState = this.submitCommentAndUpdateState.bind(this);
        const commentBoxType="Comment";
        const isFirstCommenter = totalCommentCount < 1;
        const renderCommentSortList = comments && comments.list && comments.list.length > 0;
        const broadcastAsTyping = this.props.commentConnector.broadcastAsTyping;
        const specificCommentMode = !!this.props.specificCommentId;

        /** Generate Telemetry Objects */
        this.telemetryObjects = {
            mainItem: addOrUpdateTelemetryObject(mainTelemetryObject, commentListTelemetryContracts.mainItem, {id: content && content.cmsId}, this.telemetryObjects && this.telemetryObjects.mainItem)
        };

        return (
            <React.Fragment>

                {!contentDisabled &&
                    <CommentBox { ...{onCommentBoxInitialized, componentConnector, isFirstCommenter, commentBoxType, me, t, managedClasses, submitCommentAndUpdateState, usersConnector, popupConnector, users, market, broadcastAsTyping, parentType: undefined, parentUser: undefined, content, mainTelemetryObject, theme }}/>
                }

                {renderCommentSortList &&
                    <div className={managedClasses.commentListHeader}>
                        <SortBox {...{t, managedClasses, mainTelemetryObject: this.telemetryObjects.mainItem, selectedSortType: this.state.sortType, sortTypes: CommentList.sortTypes, id: contentId}} onChange={this.getSortedComments.bind(this)}/>
                    </div>
                }

                {specificCommentMode && comments && comments.hasPrevious &&
                    <i className="cwt-link cwt-icon cwt-show-comments-btn previous" role="button" aria-pressed="false" aria-live="assertive"
                        onClick={() => commentConnector.loadPreviousComments(contentId, comments.list[0].id) }
                    >
                        {chevron_up}
                        <span className="cwt-icon-label">{t.SpecificCommentModeSeePreviousCommentsTxt}</span>
                    </i>
                }
                <ul className={managedClasses.commentListContent}>
                    {comments && comments.list && comments.list.map((item) => this.renderCommentItem(item))}
                </ul>
                {specificCommentMode && comments && comments.hasMore &&
                    <i className="cwt-link cwt-icon cwt-show-comments-btn more" role="button" aria-pressed="false" aria-live="assertive"
                        onClick={() => commentConnector.loadMoreComments(contentId, comments.list[comments.list.length-1].id) }
                    >
                        {chevron_down}
                        <span className="cwt-icon-label">{t.SpecificCommentModeSeeMoreCommentsTxt}</span>
                    </i>
                }

                {!specificCommentMode && moreCommentCount > 0 &&
                    <div aria-live="assertive">
                        {this.state.showMoreClicked ?
                            <i className="cwt-link cwt-icon cwt-show-more-comments-btn" role="button" onClick={(e) => this.showMoreClicked(e)}>
                                {chevron_down}
                                <span className="cwt-icon-label">
                                    { moreCommentCount < 2 ?
                                        t.SeeMoreCommentsTxt :
                                        t.SeeMoreCommentsPluralTxt
                                    }
                                </span>
                            </i>
                            :
                            <div className="cwt-join-conversation-container">
                                <a className="cwt-link cwt-join-conversation-btn" role="button" onClick={(e) => this.showMoreClicked(e)}>{t.SeeMoreCommentsBtn}</a>
                            </div>
                        }
                    </div>
                }
            </React.Fragment>
        );
    }

    renderCommentItem(item, isBlury?: boolean) {
        const { commentDisplayType, componentConnector, responsiveClass, contentDisabled, me, t, managedClasses, commentConnector, usersConnector, popupConnector, users, market, content, mainTelemetryObject, theme, specificCommentId } = this.props;
        const commentItemType="Comment";
        const commentItemProps = {
            componentConnector,
            commentItemType,
            item,
            contentDisabled,
            me,
            t,
            managedClasses,
            sortType: this.state.sortType,
            commentConnector,
            usersConnector,
            popupConnector,
            users,
            market,
            content,
            mainTelemetryObject,
            theme,
            responsiveClass,
            isBlury: isBlury || false,
            commentDisplayType,
            specificCommentId
        };
        if (item.skipRender) {
            return null;
        }

        return <CommentItem key={`CommentItem-${item.id}`} {...commentItemProps} />;
    }
}